/**
 * Déduplication des notifications affichées en toast quand elles arrivent par **deux canaux en
 * parallèle** : Echo/Pusher (temps réel, `useNotificationChannel`) et FCM Web (`PushSubscriber`,
 * via `onMessage` au premier plan). Le backend envoie systématiquement le push à tous les tokens
 * actifs dès qu'une notification `in_app` est créée — il n'attend pas que l'onglet soit en
 * arrière-plan pour le faire, cf. `docs/PWA_PUSH_NOTIFICATIONS.md`. Un onglet ouvert et au premier
 * plan reçoit donc les deux, quasi simultanément.
 *
 * On ne privilégie pas systématiquement l'un des deux canaux (ex. "Echo gagne toujours") : si Echo
 * est déconnecté au moment de l'envoi (reconnexion WebSocket en cours, panne Reverb passagère), le
 * push reste le seul filet et doit pouvoir s'afficher. La règle est donc purement temporelle :
 * premier arrivé, affiché ; le second, pour le même id, est tu.
 *
 * `Set` borné dans le temps plutôt que par taille : la fenêtre de rapprochement entre les deux
 * canaux se compte en centaines de ms, quelques secondes suffisent largement à couvrir la
 * variance réseau sans faire grossir la structure indéfiniment sur une session longue.
 */
const DEDUPE_WINDOW_MS = 10_000;

const recentlyShown = new Map<string, number>();

/**
 * `true` si cette notification vient d'être annoncée par l'autre canal (et doit donc être tue),
 * `false` sinon — et dans ce cas la marque comme montrée pour que l'autre canal la voie à son tour.
 */
export function claimNotificationToast(
  notificationId: string | number | undefined | null,
): boolean {
  if (notificationId == null) return false;

  const key = String(notificationId);
  const now = Date.now();

  // Nettoyage paresseux : pas de timer dédié, juste purger l'expiré à chaque appel.
  for (const [id, at] of recentlyShown) {
    if (now - at > DEDUPE_WINDOW_MS) recentlyShown.delete(id);
  }

  if (recentlyShown.has(key)) return true;

  recentlyShown.set(key, now);
  return false;
}
